import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ActionButton } from "../../components/Buttons";
import { auth } from "../../configs/firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import {
  getPreferredAuthUser,
  isSameAuthUser,
  waitForSettledAuthUser,
} from "../../utils/auth/authState";
import { trackEvent, trackMixpanelEvent } from "../../utils/Analytics";
import {
  PENDING_AUTH_INTENT_ACTIONS,
  savePendingAuthIntent,
} from "../../utils/auth/pendingAuthIntent";
import { markAnonymousGenerationUsed } from "../../utils/auth/anonymousGenerationUsage";
import { publishLettuceTalkCardToWall } from "../../utils/empathyAI/publishLettuceTalkCardToWall";
import { updateLettuceTalkCardText } from "../../utils/empathyAI/updateLettuceTalkCardText";
import { getCardGenerationWithCards } from "../../utils/firestore";
import { DEFAULT_WALL_PATH } from "../../utils/cards/cardConstants";
import AccountLinkingSheet from "./components/AccountLinkingSheet";
import EmpathyPageHeader, {
  hasSameOriginDocumentReferrer,
} from "./components/EmpathyPageHeader";
import RefineResultView from "./components/RefineResultView";
import RefineEditWordingModal from "./components/RefineEditWordingModal";
import {
  DEFAULT_REFINE_CARD_VARIANT_ID,
  REFINE_CARD_VARIANTS,
} from "./refineCardVariants";
import {
  getCardTranslationStatus,
  normalizePersistedGenerationResult,
  reconcileEditedCardResult,
  validateCardQuestion,
} from "./refineResultState";
import usePreparedCardShare from "./usePreparedCardShare";

const REFINE_DARK_BACKGROUND = "#050604";
const UNAVAILABLE_RESULT_STATE = {
  title: "These cards aren't available.",
  message:
    "They may belong to another account, or the link may no longer work.",
};

function getCreatePathForResult(result) {
  return result?.wallId
    ? `/refine?wallId=${encodeURIComponent(result.wallId)}`
    : "/refine";
}

function ResultRouteState({ title, message, authUser }) {
  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#050604] px-5 py-8 text-center font-sans text-white sm:px-8 sm:py-12">
      <Helmet>
        <meta name="theme-color" content={REFINE_DARK_BACKGROUND} />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      </Helmet>

      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-lg flex-col">
        <EmpathyPageHeader title="Your LettuceTalk Cards" authUser={authUser} />

        <main className="flex flex-1 flex-col items-center justify-center py-8">
          <h2 className="font-serif text-3xl font-medium leading-[0.95] text-white sm:text-5xl">
            {title}
          </h2>
          <p className="mt-5 text-sm font-semibold leading-relaxed text-white/62">
            {message}
          </p>
          <ActionButton
            to="/refine"
            className="mt-7 max-w-sm"
            fullWidth
            icon="plus"
          >
            Create Card
          </ActionButton>
        </main>
      </div>
    </div>
  );
}

function getResultLoadError(error) {
  if (error?.code === "permission-denied") {
    return UNAVAILABLE_RESULT_STATE;
  }

  return {
    title: "We couldn't open these cards.",
    message: "Please try again in a moment.",
  };
}

function getCardTextEditErrorMessage(error, isTranslationRetry = false) {
  if (isTranslationRetry) {
    return "We couldn't update the translation. Please try again.";
  }

  const messageByCode = {
    forbidden: "You can't edit this card.",
    "not-found": "This card is no longer available.",
    unauthenticated: "Please try again to save your wording.",
    conflict: "This card changed elsewhere. Please try again.",
  };

  return (
    messageByCode[error?.code] ||
    "We couldn't save your wording. Please try again."
  );
}

function getCleanString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function getVariantIdForCardId(result, cardId) {
  const cleanCardId = getCleanString(cardId);

  if (!cleanCardId || !result?.generatedCardIdsByVariant) {
    return "";
  }

  const matchingVariant = Object.entries(result.generatedCardIdsByVariant).find(
    ([, generatedCardId]) => generatedCardId === cleanCardId
  );

  return matchingVariant?.[0] || "";
}

function ResultView() {
  const { generationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const expectedOwnerUid = getCleanString(location.state?.expectedOwnerUid);
  const immediateResult = location.state?.immediateResult || null;
  const requestedCardId = getCleanString(searchParams.get("cardId"));
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(
    DEFAULT_REFINE_CARD_VARIANT_ID
  );
  const hasChangedSelectionRef = useRef(false);
  const trackedSelectionRef = useRef("");
  const {
    clearShareError,
    closeSharePreview,
    copyShareLink,
    openShareSheet,
    prepareShare,
    resetShareState,
    shareError,
    sharePreview,
    sharing,
    updateSharePreviewMessage,
  } = usePreparedCardShare({ source: "Result" });
  const [addingToWall, setAddingToWall] = useState(false);
  const [wallCardIdsByVariant, setWallCardIdsByVariant] = useState({});
  const [wallError, setWallError] = useState("");
  const [editingCardId, setEditingCardId] = useState("");
  const [editDraft, setEditDraft] = useState("");
  const [editError, setEditError] = useState("");
  const [translationError, setTranslationError] = useState("");
  const [isSavingCardText, setIsSavingCardText] = useState(false);
  const [isRetryingTranslation, setIsRetryingTranslation] = useState(false);
  const [isAccountLinkingOpen, setIsAccountLinkingOpen] = useState(false);
  const isAccountLinkingOpenRef = useRef(false);
  const resultRef = useRef(null);

  useLayoutEffect(() => {
    const rootElement = document.getElementById("root");
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    let statusBarMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]'
    );
    const createdThemeColorMeta = !themeColorMeta;
    const createdStatusBarMeta = !statusBarMeta;
    const previousHtmlBackground = document.documentElement.style.backgroundColor;
    const previousBodyBackground = document.body.style.backgroundColor;
    const previousRootBackground = rootElement?.style.backgroundColor || "";
    const previousThemeColor = themeColorMeta?.getAttribute("content") || "";
    const previousStatusBarStyle =
      statusBarMeta?.getAttribute("content") || "";

    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.setAttribute("name", "theme-color");
      document.head.appendChild(themeColorMeta);
    }

    if (!statusBarMeta) {
      statusBarMeta = document.createElement("meta");
      statusBarMeta.setAttribute(
        "name",
        "apple-mobile-web-app-status-bar-style"
      );
      document.head.appendChild(statusBarMeta);
    }

    document.documentElement.style.backgroundColor = REFINE_DARK_BACKGROUND;
    document.body.style.backgroundColor = REFINE_DARK_BACKGROUND;
    themeColorMeta.setAttribute("content", REFINE_DARK_BACKGROUND);
    statusBarMeta.setAttribute("content", "black");

    if (rootElement) {
      rootElement.style.backgroundColor = REFINE_DARK_BACKGROUND;
    }

    return () => {
      document.documentElement.style.backgroundColor = previousHtmlBackground;
      document.body.style.backgroundColor = previousBodyBackground;

      if (rootElement) {
        rootElement.style.backgroundColor = previousRootBackground;
      }

      if (createdThemeColorMeta) {
        themeColorMeta.remove();
      } else {
        themeColorMeta.setAttribute("content", previousThemeColor);
      }

      if (createdStatusBarMeta) {
        statusBarMeta.remove();
      } else {
        statusBarMeta.setAttribute("content", previousStatusBarStyle);
      }
    };
  }, []);

  useEffect(() => {
    isAccountLinkingOpenRef.current = isAccountLinkingOpen;
  }, [isAccountLinkingOpen]);

  useEffect(() => {
    resultRef.current = result;
  }, [result]);

  useEffect(() => {
    hasChangedSelectionRef.current = false;
    trackedSelectionRef.current = "";
    let isMounted = true;

    async function loadResult() {
      if (isAccountLinkingOpenRef.current && resultRef.current) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadState(null);
      setResult(null);
      setSelectedVariantId(DEFAULT_REFINE_CARD_VARIANT_ID);
      resetShareState({ clearPreparedUrls: true });
      setWallCardIdsByVariant({});
      setWallError("");
      setEditingCardId("");
      setEditDraft("");
      setEditError("");
      setTranslationError("");
      setIsSavingCardText(false);
      setIsRetryingTranslation(false);
      setIsAccountLinkingOpen(false);

      if (!generationId) {
        setLoadState(UNAVAILABLE_RESULT_STATE);
        setLoading(false);
        return;
      }

      const initializedCurrentUser = await waitForSettledAuthUser({
        contextUser: currentUser,
        expectedUid: expectedOwnerUid,
      });

      if (!isMounted) return;

      if (!initializedCurrentUser) {
        setLoadState(UNAVAILABLE_RESULT_STATE);
        setLoading(false);
        return;
      }

      try {
        const persistedGeneration = await getCardGenerationWithCards(
          generationId
        );

        if (!isMounted) return;

        const latestUser = getPreferredAuthUser(auth.currentUser, currentUser);

        if (!isSameAuthUser(latestUser, initializedCurrentUser)) {
          setLoadState(UNAVAILABLE_RESULT_STATE);
          return;
        }

        if (!persistedGeneration?.generation) {
          setLoadState(UNAVAILABLE_RESULT_STATE);
          return;
        }

        if (
          persistedGeneration.generation.ownerUid !==
          initializedCurrentUser.uid
        ) {
          setLoadState(UNAVAILABLE_RESULT_STATE);
          return;
        }

        const normalizedResult = normalizePersistedGenerationResult(
          persistedGeneration,
          immediateResult?.translationLanguage || "",
          immediateResult
        );

        if (!normalizedResult) {
          setLoadState(UNAVAILABLE_RESULT_STATE);
          return;
        }

        setResult(normalizedResult);
        setSelectedVariantId(
          getVariantIdForCardId(normalizedResult, requestedCardId) ||
            normalizedResult.cardVariants?.[0]?.id ||
            DEFAULT_REFINE_CARD_VARIANT_ID
        );

        if (initializedCurrentUser.isAnonymous === true) {
          markAnonymousGenerationUsed(initializedCurrentUser);
        }
      } catch (error) {
        if (!isMounted) return;

        console.error("Unable to load persisted result", error);
        setLoadState(getResultLoadError(error));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadResult();

    return () => {
      isMounted = false;
    };
  }, [
    currentUser,
    generationId,
    immediateResult,
    expectedOwnerUid,
    requestedCardId,
    resetShareState,
  ]);

  const getVariantQuestion = (variantId) =>
    result?.variantQuestions?.[variantId] || result?.refinedQuestion || "";
  const finalQuestion = getVariantQuestion(selectedVariantId);
  const activeUser = getPreferredAuthUser(auth.currentUser, currentUser);
  const selectedCard = result?.cardsByType?.[selectedVariantId] || null;
  const resultCardVariants = result?.cardVariants || REFINE_CARD_VARIANTS;
  const selectedCardId = getCleanString(selectedCard?.cardId || selectedCard?.id);
  const selectedCardTranslationStatus = getCardTranslationStatus(selectedCard);
  const hasConfiguredTranslations = Boolean(
    result?.generation?.translationLanguages?.length ||
      selectedCard?.translations?.length ||
      selectedCard?.translation
  );
  const isEditEligible = Boolean(
    activeUser?.uid &&
      selectedCard?.schemaVersion === 3 &&
      selectedCard?.ownerUid === activeUser.uid &&
      selectedCardId
  );
  const isEditingCardText = Boolean(editingCardId);
  const isEditInFlight = isSavingCardText || isRetryingTranslation;
  const isInteractionDisabled = isEditInFlight || isEditingCardText;
  const selectedWallCardId = wallCardIdsByVariant[selectedVariantId] || "";
  const isWallSubmissionEnabled = Boolean(result?.wallId);
  const wallPath = result?.wallId
    ? `/wall/${encodeURIComponent(result.wallId)}`
    : DEFAULT_WALL_PATH;
  const createPath = getCreatePathForResult(result);
  const isAnonymousResultUser =
    getPreferredAuthUser(auth.currentUser, currentUser)?.isAnonymous === true;

  const selectVariant = (variantId) => {
    if (isInteractionDisabled) return;

    if (variantId !== selectedVariantId) {
      hasChangedSelectionRef.current = true;
    }
    setSelectedVariantId(variantId);
    clearShareError();
    setWallError("");
  };

  const getVariantTranslation = (variantId) => {
    const card = result?.cardsByType?.[variantId];

    if (getCardTranslationStatus(card) === "needs_refresh") {
      return "";
    }

    return card?.translation || result?.translation || "";
  };

  const getVariantTranslations = (variantId) => {
    const card = result?.cardsByType?.[variantId];

    if (getCardTranslationStatus(card) === "needs_refresh") {
      return [];
    }

    return Array.isArray(card?.translations)
      ? card.translations
      : [];
  };

  const getSelectedGeneratedCardId = () => {
    const generatedCardId =
      result?.generatedCardIdsByVariant?.[selectedVariantId];

    return typeof generatedCardId === "string" ? generatedCardId.trim() : "";
  };

  const trackCommittedCardOption = () => {
    const cardId = getSelectedGeneratedCardId();
    const committedGenerationId = result?.generationId || generationId;
    if (!cardId || !committedGenerationId) return;

    const selectionKey = `${committedGenerationId}:${cardId}`;
    if (trackedSelectionRef.current === selectionKey) return;

    trackedSelectionRef.current = selectionKey;
    const cardPosition = resultCardVariants.findIndex(
      ({ id }) => id === selectedVariantId
    );
    trackMixpanelEvent("Card Option Selected", {
      generation_id: committedGenerationId,
      card_id: cardId,
      card_variant: selectedVariantId,
      card_position: cardPosition >= 0 ? cardPosition + 1 : undefined,
      selection_method: hasChangedSelectionRef.current ? "changed" : "default",
    });
  };

  const openCardTextEditor = () => {
    if (!isEditEligible || isEditInFlight) return;

    setEditingCardId(selectedCardId);
    setEditDraft(finalQuestion);
    setEditError("");
  };

  const closeCardTextEditor = () => {
    if (isSavingCardText) return;

    setEditingCardId("");
    setEditDraft("");
    setEditError("");
  };

  const saveCardText = async () => {
    if (isSavingCardText || !editingCardId) return;

    const validation = validateCardQuestion(editDraft, finalQuestion);

    if (!validation.valid) {
      setEditError(validation.message);
      return;
    }

    setIsSavingCardText(true);
    setEditError("");
    setTranslationError("");

    try {
      const update = await updateLettuceTalkCardText({
        cardId: editingCardId,
        editedText: validation.value,
      });

      setResult((previousResult) =>
        reconcileEditedCardResult(previousResult, {
          cardId: editingCardId,
          questionRefined: update.questionRefined,
          translations: update.translations,
          translationStatus: update.translationStatus,
          manualEditsCount: update.manualEditsCount,
        })
      );
      clearShareError();
      setWallError("");
      setTranslationError("");
      trackCommittedCardOption();
      setEditingCardId("");
      setEditDraft("");
    } catch (error) {
      setEditError(getCardTextEditErrorMessage(error));
    } finally {
      setIsSavingCardText(false);
    }
  };

  const retryTranslation = async () => {
    if (
      isRetryingTranslation ||
      !selectedCardId ||
      selectedCardTranslationStatus !== "needs_refresh"
    ) {
      return;
    }

    setIsRetryingTranslation(true);
    setTranslationError("");

    try {
      const update = await updateLettuceTalkCardText({
        cardId: selectedCardId,
        editedText: finalQuestion,
      });

      setResult((previousResult) =>
        reconcileEditedCardResult(previousResult, {
          cardId: selectedCardId,
          questionRefined: update.questionRefined,
          translations: update.translations,
          translationStatus: update.translationStatus,
          manualEditsCount: update.manualEditsCount,
        })
      );
      clearShareError();
      setWallError("");
      setTranslationError("");
    } catch (error) {
      setTranslationError(getCardTextEditErrorMessage(error, true));
    } finally {
      setIsRetryingTranslation(false);
    }
  };

  const createAnother = () => {
    if (!activeUser?.isAnonymous) {
      navigate(createPath);
      return;
    }

    savePendingAuthIntent({
      action: PENDING_AUTH_INTENT_ACTIONS.KEEP_CARDS,
      cardId: getSelectedGeneratedCardId(),
      generationId: result?.generationId || generationId,
      returnTo: "/your-cards?saved=1",
      partner: result?.partnerId,
      draft: {
        rawThought: result?.rawThought,
        translationLanguage: result?.translationLanguage,
      },
    });
    setIsAccountLinkingOpen(true);
  };

  const navigateBackFromResult = () => {
    if (
      location.state?.fromLettuceTalk === true ||
      hasSameOriginDocumentReferrer()
    ) {
      navigate(-1);
      return;
    }

    navigate(createPath);
  };

  const continueAfterAccountLinking = async ({
    authResult,
    pendingIntent,
  } = {}) => {
    const returnTo =
      pendingIntent?.action === PENDING_AUTH_INTENT_ACTIONS.KEEP_CARDS
        ? "/your-cards?saved=1"
        : createPath;
    const savedGenerationId =
      typeof pendingIntent?.generationId === "string" &&
      pendingIntent.generationId.trim()
        ? pendingIntent.generationId.trim()
        : result?.generationId || generationId;
    const expectedOwnerUid =
      typeof authResult?.uid === "string" && authResult.uid.trim()
        ? authResult.uid.trim()
        : "";

    setIsAccountLinkingOpen(false);
    navigate(
      returnTo,
      returnTo === "/your-cards?saved=1" && savedGenerationId
        ? { state: { savedGenerationId, expectedOwnerUid } }
      : undefined
    );
  };

  const share = async () => {
    if (!result || isInteractionDisabled) return;

    trackCommittedCardOption();
    await prepareShare({
      cardId: getSelectedGeneratedCardId(),
      question: finalQuestion,
    });
  };

  const addToLivingWall = async () => {
    if (!result || addingToWall || isInteractionDisabled) return;

    if (!isWallSubmissionEnabled) {
      setWallError("This page is not connected to a public wall yet.");
      return;
    }

    const trimmedQuestion = finalQuestion?.trim();

    if (!trimmedQuestion) {
      setWallError("Add a question before adding this card to the Living Wall.");
      return;
    }

    const persistedCardId = getSelectedGeneratedCardId();

    if (!persistedCardId) {
      setWallError(
        "We couldn't find the generated card to add. Please create the cards again and try once more."
      );
      trackEvent("CTA", "Add to wall failed", "Result missing persisted card");
      return;
    }

    setAddingToWall(true);
    setWallError("");
    trackCommittedCardOption();
    trackEvent("CTA", "Add to wall started", "Result");

    try {
      const publishedCard = await publishLettuceTalkCardToWall({
        cardId: persistedCardId,
        wallId: result.wallId,
      });
      const cardId = publishedCard.cardId || persistedCardId;

      setWallCardIdsByVariant((cardIdsByVariant) => ({
        ...cardIdsByVariant,
        [selectedVariantId]: cardId,
      }));
      trackEvent("CTA", "Add to wall completed", cardId);
      navigate(wallPath, {
        state: { addedCardId: cardId, wallId: result.wallId },
      });
    } catch (error) {
      console.error("Unable to add card to wall", error);
      setWallError(
        error.status === 401 || error.status === 403
          ? "We couldn't add this card to the wall yet. Please try again in a moment."
          : error.code === "auth/admin-restricted-operation"
            ? "Anonymous access isn't enabled yet. Please try again later."
            : "Couldn't add this card to the wall. Please try again."
      );
      trackEvent("CTA", "Add to wall failed", "Result");
    } finally {
      setAddingToWall(false);
    }
  };

  if (loading) {
    return (
      <ResultRouteState
        title="Opening your cards..."
        message="Loading your saved card result."
        authUser={activeUser}
      />
    );
  }

  if (loadState || !result) {
    return (
      <ResultRouteState
        title={loadState?.title || "This result is unavailable"}
        message={
          loadState?.message ||
          "They may belong to another account, or the link may no longer work."
        }
        authUser={activeUser}
      />
    );
  }

  return (
    <div
      data-testid="result-scroll-container"
      className="h-screen h-[100dvh] overflow-x-hidden overflow-y-auto overscroll-y-contain bg-[#050604] font-sans text-white [-webkit-overflow-scrolling:touch]"
    >
      <Helmet>
        <meta name="theme-color" content={REFINE_DARK_BACKGROUND} />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      </Helmet>

      <main className="relative">
        <section className="mx-auto max-w-5xl px-5 pb-28 pt-8 sm:px-8 sm:pt-12">
          <RefineResultView
            cardVariants={resultCardVariants}
            selectedVariantId={selectedVariantId}
            onSelectVariant={selectVariant}
            result={result}
            rawThought={result.rawThought}
            getVariantQuestion={getVariantQuestion}
            getVariantTranslation={getVariantTranslation}
            getVariantTranslations={getVariantTranslations}
            isEditEligible={isEditEligible}
            isInteractionDisabled={isInteractionDisabled}
            onToggleEditing={openCardTextEditor}
            translationStatus={selectedCardTranslationStatus}
            onRetryTranslation={retryTranslation}
            retryingTranslation={isRetryingTranslation}
            authUser={activeUser}
            onBackToPrompt={navigateBackFromResult}
            showBackButton
            sharePreview={sharePreview}
            onSharePreviewMessageChange={updateSharePreviewMessage}
            onCloseSharePreview={closeSharePreview}
            onOpenShareSheet={openShareSheet}
            onPrepareShareLink={share}
            onCopyShareLink={copyShareLink}
            onShare={share}
            onAddToLivingWall={addToLivingWall}
            isWallSubmissionEnabled={isWallSubmissionEnabled}
            addingToWall={addingToWall}
            sharing={sharing}
            wallCardId={selectedWallCardId}
            wallPath={wallPath}
            accountCtaTitle={
              isAnonymousResultUser
                ? "Create a free account to save your cards and make more"
                : "Create more cards"
            }
            accountCtaHelper=""
            isAccountCtaAnonymous={isAnonymousResultUser}
            onAccountCtaClick={createAnother}
            shareError={shareError}
            wallError={wallError}
            translationError={translationError}
            refineError=""
          />
          {isEditingCardText ? (
            <RefineEditWordingModal
              isOpen
              value={editDraft}
              savedValue={finalQuestion}
              onChange={(value) => {
                setEditDraft(value);
                setEditError("");
              }}
              onClose={closeCardTextEditor}
              onSave={saveCardText}
              saving={isSavingCardText}
              savingLabel={
                hasConfiguredTranslations ||
                selectedCardTranslationStatus === "needs_refresh"
                  ? "Updating translations…"
                  : "Saving…"
              }
              error={editError}
            />
          ) : null}
          <AccountLinkingSheet
            isOpen={isAccountLinkingOpen}
            intent="keep-cards"
            mergeContext={{
              cardId: getSelectedGeneratedCardId(),
              generationId: result?.generationId || generationId,
            }}
            onDismiss={() => setIsAccountLinkingOpen(false)}
            onLinked={continueAfterAccountLinking}
          />
        </section>
      </main>
    </div>
  );
}

export default ResultView;
